import { v4 as uuidv4 } from "uuid";

import type { ICartItem, ICustomerCart } from "@/data/model/Customer";
import { CartControllerMode } from "@/data/model/Enums";
import { Format } from "@/data/util/Format";

import { clientLanguage } from "@/shared/localization/language";
import { translations } from "@/shared/localization/translations";
import { logError } from "@/shared/logging/errors";
import { Ripple } from "@/shared/ui/Ripple";
import { toMoneyWithSign } from "@/shared/utils/format";

import { TastierClient } from "@/app/TastierClient";
import { BaseController } from "@/app/controller/BaseController";
import { PayButton } from "@/app/elements/PayButton";
import { appEvents } from "@/app/events/appEvents";
import { trans } from "@/app/localization/Trans";
import { MinimumAgePopover } from "@/app/popover/MinimumAgePopover";
import { Popup } from "@/app/ui/Popup";
import { UserMenu } from "@/app/ui/UserMenu";

// todo: to remove
export class CartController extends BaseController {
    public onContinue?: { () };
    public onGoBack?: { () };

    private $head: JQuery;
    private $itemList: JQuery;

    private $bottom: JQuery;
    private $selectionSum: JQuery;
    private proceedBtn: PayButton;
    private currentMode: CartControllerMode;

    public userMenu: UserMenu;

    private isPerformingAction = false;

    constructor(mode: CartControllerMode) {
        super("cart");
        this.currentMode = mode;

        this.$head = $('<div class="head"><div class="title">' + trans.cart.title + "</div></div>").appendTo(
            this.$view
        );

        Ripple.bind($('<a class="action left" href="#"><i class="fal fa-arrow-left"></i></a>'))
            .prependTo(this.$head)
            .click((e) => {
                e.preventDefault();
                if (this.onGoBack !== undefined) this.onGoBack();
            });

        this.userMenu = new UserMenu().appendTo(this.$head);
        this.$itemList = $('<div class="itemlist scrollable" />').appendTo(this.$view);

        this.$bottom = $('<div class="bottom" />').appendTo(this.$view);
        this.$selectionSum = $('<div class="selectionsum" />').appendTo(this.$bottom);
        if (mode === CartControllerMode.Order) {
            this.proceedBtn = new PayButton(trans.general.confirm)
                .setAmount(toMoneyWithSign(0, true))
                .setDisabled()
                .appendTo(this.$bottom);
            this.proceedBtn.onClick = () => {
                this.checkMinimumAgeAndContinue();
            };
        } else {
            this.proceedBtn = new PayButton(trans.pay.orderWithStaff).setDisabled().appendTo(this.$bottom);
        }
    }

    public updateMode(mode: CartControllerMode) {
        this.currentMode = mode;
        if (mode === CartControllerMode.Order) {
            this.proceedBtn.setAction(trans.general.confirm).setAmount(toMoneyWithSign(0, true));
            this.proceedBtn.onClick = () => {
                this.checkMinimumAgeAndContinue();
            };
        }

        if (mode === CartControllerMode.Offer) {
            this.proceedBtn.setAction(trans.pay.orderWithStaff).setAmount(toMoneyWithSign(0)).setDisabled();
            this.proceedBtn.onClick = undefined;
        }
    }

    protected onBecomeVisible() {
        TastierClient.context.onCartChanged.add(this.updateCart, this);
        this.$itemList.scrollTop(0);

        this.updateCart(true);
    }

    protected onBecomeInvisible() {
        TastierClient.context.onCartChanged.remove(this.updateCart, this);
    }

    private updateCart(isUserAction: boolean) {
        const offer = TastierClient.context.currentOffer;
        let articles = 0;
        const minHint = "";
        if (this.currentMode === CartControllerMode.Order && offer !== undefined && offer.cart !== undefined) {
            this.proceedBtn.setAmount(toMoneyWithSign(offer.cart.total));
            if (offer.cart.items.length > 0 && offer.cart.total >= 0) this.proceedBtn.setEnabled();
            else {
                this.proceedBtn.setDisabled();
            }
        } else {
            this.proceedBtn.setDisabled();
        }

        if (offer !== undefined && offer.cart !== undefined) {
            for (const item of offer.cart.items) {
                articles += item.quantity;
            }
        }

        this.$selectionSum.html(trans.cart.itemsSelected.replace("{0}", articles + "") + minHint);

        if (!isUserAction)
            Popup.show(
                "<b>" +
                    Format.htmlEscape(translations.pay.cartChangedTitle) +
                    "</b><br /><br />" +
                    Format.htmlEscape(translations.pay.cartChangedMessage),
                undefined,
                undefined,
                undefined,
                "totalChanged"
            );

        const lastScrollPos = this.$itemList.scrollTop();

        this.$itemList.empty();

        if (offer === undefined || offer.cart === undefined || offer.cart.items.length === 0) {
            const nodata = $('<div class="nodata" />').appendTo(this.$itemList.empty());
            nodata.append('<div class="lbl">' + trans.cart.noItems + "</div>");
            $('<a class="backlink">' + trans.cart.backToOverview + "</div>")
                .appendTo(nodata)
                .click(() => {
                    if (this.onGoBack !== undefined) this.onGoBack();
                });

            return;
        }

        for (const item of offer.cart.items) {
            this.renderCartItem(item).appendTo(this.$itemList);
        }

        this.updateCommentEditors(offer.cart);

        this.$itemList.scrollTop(lastScrollPos);
    }

    private renderCartItem(model: ICartItem): JQuery {
        const item = $('<div class="item" />').data("model", model);
        const head = $('<div class="head" />').appendTo(item);

        head.append('<div class="quantity">' + model.quantity + "x</div>");
        head.append(
            '<div class="name">' + Format.htmlEscape(Format.translate(model.name, clientLanguage())) + "</div>"
        );

        const increaser = $('<div class="increaser" />').appendTo(head);

        const cartItemMinQuantity = model.minQuantity ?? 1;
        const cartItemMaxQuantity = model.maxQuantity;
        const decreaseBtn = Ripple.bindRippleOut(
            $(
                '<button type="button" class="btn decrease"><div class="outer"><i class="fas fa-minus-circle"></i></div></button'
            )
                .appendTo(increaser)
                .click(async (e) => {
                    if (this.isPerformingAction) {
                        e.stopPropagation();
                        return;
                    }

                    const b = $(e.currentTarget).prop("disabled", true);
                    const currentQuantity = b.parent().parent().parent().data("model").quantity;
                    if (currentQuantity <= cartItemMinQuantity) return;

                    this.isPerformingAction = true;
                    try {
                        await TastierClient.context.changeCartItemQuantity(model.id, currentQuantity - 1);
                    } catch (ex) {
                        logError(ex);
                    } finally {
                        this.isPerformingAction = false;
                        b.prop("disabled", false);
                    }
                }),
            ".outer"
        );

        if (model.quantity <= cartItemMinQuantity) decreaseBtn.addClass("disabled").prop("disabled", true);

        const increaseBtn = Ripple.bindRippleOut(
            $(
                '<button type="button" class="btn increase"><div class="outer"><i class="fas fa-plus-circle"></i></div></button'
            )
                .appendTo(increaser)
                .click(async (e) => {
                    if (this.isPerformingAction) {
                        e.stopPropagation();
                        return;
                    }

                    const b = $(e.currentTarget).prop("disabled", true);
                    const currentQuantity = b.parent().parent().parent().data("model").quantity;
                    if (cartItemMaxQuantity !== undefined && currentQuantity >= cartItemMaxQuantity) return;

                    this.isPerformingAction = true;
                    try {
                        await TastierClient.context.changeCartItemQuantity(model.id, currentQuantity + 1);
                    } catch (ex) {
                        logError(ex);
                    } finally {
                        this.isPerformingAction = false;
                        b.prop("disabled", false);
                    }
                }),
            ".outer"
        );

        if (model.quantity >= cartItemMaxQuantity!) increaseBtn.addClass("disabled").prop("disabled", true);

        if (cartItemMaxQuantity == cartItemMinQuantity) {
            increaser.addClass("hidden");
        } else {
            increaser.removeClass("hidden");
        }

        head.append(
            '<div class="price">' +
                (model.singlePrice === 0 ? "" : (model.quantity * model.singlePrice).toFixed(2)) +
                "</div>"
        );

        Ripple.bindRippleOut(
            $('<div class="remover"><div class="outer"><i class="fal fa-trash-alt"></i></div></div>')
                .appendTo(head)
                .click(async (e) => {
                    if (this.isPerformingAction) {
                        e.stopPropagation();
                        return;
                    }

                    const b = $(e.currentTarget).prop("disabled", true);

                    this.isPerformingAction = true;
                    try {
                        await TastierClient.context.removeItemFormCart(model.id);
                    } catch (ex) {
                        logError(ex);
                    } finally {
                        this.isPerformingAction = false;
                        b.prop("disabled", false);
                    }
                }),
            "> .outer"
        );

        this.recursiveAppendOptions(item, model, model.quantity);

        this.renderCommentEditor(item, model);

        return item;
    }

    private recursiveAppendOptions($container: JQuery, item: ICartItem, multiplier: number) {
        if (item.options.length === 0) return;

        const subContainer = $('<div class="subs" />').appendTo($container);

        for (const optionGroup of item.options) {
            const groupContainer = $(
                '<div class="group"><div class="name">' +
                    Format.htmlEscape(Format.translate(optionGroup.name, clientLanguage())) +
                    "</div></div>"
            ).appendTo(subContainer);
            const optionsContainer = $('<div class="entries" />').appendTo(groupContainer);

            for (const option of optionGroup.items) {
                const optionContainer = $('<div class="item" />').appendTo(optionsContainer);
                const optionHead = $('<div class="head" />').appendTo(optionContainer);

                const nextMultiplier = multiplier * option.quantity;

                optionHead.append(
                    '<div class="name">' +
                        (option.quantity === 1 ? "" : '<div class="quantity">' + option.quantity + "x</div>") +
                        Format.htmlEscape(Format.translate(option.name, clientLanguage())) +
                        "</div>"
                );
                optionHead.append(
                    '<div class="price">' +
                        (option.singlePrice === 0 ? "" : (nextMultiplier * option.singlePrice).toFixed(2)) +
                        "</div>"
                );

                this.recursiveAppendOptions(optionContainer, option, nextMultiplier);
            }
        }
    }

    private checkMinimumAgeAndContinue() {
        const offer = TastierClient.context.currentOffer;
        if (offer === undefined || offer.cart === undefined) return;

        let minAge = 0;

        const searchMinAgeRecusrive = (itm: ICartItem) => {
            if (itm.minAge !== undefined && itm.minAge > minAge) minAge = itm.minAge;

            if (itm.options !== undefined && itm.options.length > 0) {
                for (const optionGroup of itm.options) {
                    for (const optionItem of optionGroup.items) {
                        searchMinAgeRecusrive(optionItem);
                    }
                }
            }
        };

        for (const item of offer.cart.items) {
            searchMinAgeRecusrive(item);
        }

        if (minAge === 0) {
            if (this.onContinue !== undefined) this.onContinue();

            return;
        }

        new MinimumAgePopover(minAge, () => {
            if (this.onContinue !== undefined) this.onContinue();
        }).show();
    }

    // #region Comment Editor
    private renderCommentEditor(item: JQuery, model: ICartItem) {
        // todo: check if is comment allowed
        $(`<div class="comment-editor-container cart-item-comment-${model.id}"></div>`).appendTo(item);
    }

    private updateCommentEditors(cart: ICustomerCart) {
        // todo: so much overengineering here, to not refactor whole view to vue component...
        // we set the id of the item list to a random hash,
        // so we can update the comment editors in CartItemCommentEditorsContainer.vue
        const updateHash = `cart-update-id-${uuidv4()}`;
        this.$itemList.attr("id", updateHash);

        appEvents.cartController.cartUpdated.dispatch({
            cart,
            updateHash,
        });
    }
    // #endregion
}
